import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import RenderDirection from "../as2-communication-status-component/as2-summary-table/RenderDirection";
import Alert from "@mui/material/Alert";

import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionItem,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import RenderStatus from "../as2-communication-status-component/as2-summary-table/RenderStatus";
import {
  useAs2Client,
  useClient,
} from "../as2-onboarding-component/hooks/useClient";
import CheckCircle from "@mui/icons-material/CheckCircle";
import {
  getDiffDays,
  getExpirationLabel,
  extractHostnamePort,
  getDirectionLabel,
} from "../utils/index";

const As2TPDetails = (props) => {
  const {
    envelope,
    as2TPIdentifier,
    as2TPProfileName,
    supplierNum,
    envelopes,
  } = props;
  const { oktaAuth } = useOktaAuth();
  const as2Client = useAs2Client(oktaAuth.getAccessToken());
  const sterlingClient = useClient(oktaAuth.getAccessToken());

  const sortCerts = (certs) => {
    if (certs.length > 1) {
      return certs.sort((a, b) => {
        if (a.certificateOrder > b.certificateOrder) {
          return 1;
        } else if (b.certificateOrder > a.certificateOrder) {
          return -1;
        } else {
          return 0;
        }
      });
    } else {
      return certs;
    }
  };
  const emptyCert = {
    certificateOrder: 1,
    certName: null,
    goLiveDate: null,
    notAfterDate: null,
  };

  const emptyMessage = {
    visible: false,
    message: "",
  };

  const initialAs2Details = {
    found: null,
    envelopeName: null,
    supplierNum: null,
    direction: null,
    as2Identifier: null,
    identityName: null,
    profileName: null,
    caCertificate: [emptyCert],
    endPoint: null,
    exchangeCertificate: [emptyCert],
    signingCertificate: [emptyCert],
    cipherStrength: {
      code: null,
      display: null,
    },
    deliveryMode: {
      code: null,
      display: null,
    },
    mdnReceipt: {
      code: null,
      display: null,
    },
    ssl: {
      code: null,
      display: null,
    },
    signingAlgorithm: {
      code: null,
      display: null,
    },
  };
  const [as2Details, setAs2Details] = useState(initialAs2Details);
  const [displayMessage, setDisplayMessage] = useState(emptyMessage);
  const [remoteCACert, setRemoteCACert] = useState(emptyCert);
  const [errorRemoteCACert, setErrorRemoteCACert] = useState("");

  let as2ProfileData = {};

  const processFetchAsProfile = (response) => {
    if (response.data.length > 0) {
      as2ProfileData = response.data[0];
      return true;
    } else {
      setAs2Details({
        envelopeName: envelope,
      });
      setDisplayMessage({
        visible: true,
        message:
          "The selected envelope could not be associated with an AS2 connection.",
      });
      return false;
    }
  };

  const processFetchSterlingAs2Profile = (response) => {
    let sterlingAs2Profile = response.data[0];
    if (sterlingAs2Profile.caCertificate.length === 0) {
      sterlingAs2Profile.caCertificate.push(emptyCert);
    } else {
      sterlingAs2Profile.caCertificate = sortCerts(
        sterlingAs2Profile.caCertificate
      );
    }
    if (sterlingAs2Profile.exchangeCertificate.length === 0) {
      sterlingAs2Profile.exchangeCertificate.push(emptyCert);
    } else {
      sterlingAs2Profile.exchangeCertificate = sortCerts(
        sterlingAs2Profile.exchangeCertificate
      );
    }
    if (sterlingAs2Profile.signingCertificate.length === 0) {
      sterlingAs2Profile.signingCertificate.push(emptyCert);
    } else {
      sterlingAs2Profile.signingCertificate = sortCerts(
        sterlingAs2Profile.signingCertificate
      );
    }

    setAs2Details({
      envelopeName: envelope,
      found: true,
      ...as2ProfileData,
      ...sterlingAs2Profile,
    });
    setDisplayMessage(emptyMessage);
  };
  const processFetchError = (reason, message) => {
    console.error(reason);
    setAs2Details({
      envelopeName: envelope,
    });
    setDisplayMessage({
      visible: true,
      message: message,
    });
  };

  useEffect(() => {
    setErrorRemoteCACert("");
    setRemoteCACert(emptyCert);
    const fetchAs2ProfileData = async (envelopeName) => {
      return await as2Client.get(
        "/api/as2/trading-partner/profile?envelopeName=" + envelopeName
      );
    };

    const fetchSterlingAs2ProfileData = async (as2Identifier, profileName) => {
      return await sterlingClient.get(
        `/B2BAPIs/svc/as2tradingpartners/?_range=0-999&as2Identifier=${as2Identifier}&identityName=&profileName=${profileName}`
      );
    };

    if (envelope !== undefined) {
      fetchAs2ProfileData(envelope)
        .then((response) => {
          if (processFetchAsProfile(response)) {
            const as2Identifier =
              as2ProfileData.direction === "I"
                ? as2ProfileData.senderAs2Id
                : as2ProfileData.receiverAs2Id;
            const as2ProfileName = as2ProfileData.tradingPartnerProfile;
            fetchSterlingAs2ProfileData(as2Identifier, as2ProfileName)
              .then((response) => {
                processFetchSterlingAs2Profile(response);
              })
              .catch((reason) => {
                processFetchError(
                  reason,
                  "An unexpected error occurred when fetching AS2 Details from Sterling AS2 API, please try again."
                );
              });
          }
        })
        .catch((reason) => {
          processFetchError(
            reason,
            "An unexpected error occurred when fetching AS2 Details from AS2 API, please try again."
          );
        });
    } else if (as2TPProfileName && as2TPIdentifier) {
      fetchSterlingAs2ProfileData(as2TPIdentifier, as2TPProfileName)
        .then((response) => {
          processFetchSterlingAs2Profile(response);
        })
        .catch((reason) => {
          processFetchError(
            reason,
            "An unexpected error occurred when fetching AS2 Details from Sterling AS2 API, please try again."
          );
        });
    }
  }, [envelope, as2TPIdentifier, as2TPProfileName]);

  return (
    <Box>
      {displayMessage.visible === true && (
        <Grid container size={{xs:12}}>
          <Grid item size={{xs:2}}>
            <Alert
              severity="warning"
              title={`No AS2 Details found for envelope ${as2Details.envelopeName}`}
            >
              {displayMessage.message}
            </Alert>
          </Grid>
        </Grid>
      )}
      <Grid alignItems="flex-start" container justifyContent="left">
        {errorRemoteCACert !== "" && (
          <Grid container mb={"10px"}>
            <Grid item size={{xs:2}}>
              <Alert
                severity="warning"
                title={`Error while validating remote certificate.`}
              >
                {errorRemoteCACert}
              </Alert>
            </Grid>
          </Grid>
        )}
        <Grid container size={{xs:12}}>
          <Grid item size={{xs:2}}>
            {/*<Typography size="xl" component="p" fontWeight="bold" gutterBottom>
                                {as2Details.envelopeName ? `AS2 Connection Details - ${as2Details.envelopeName}` : "AS2 Connection Details"}
                            </Typography>*/}
            <Typography size="xl" component="p" fontWeight="bold" gutterBottom>
              AS2 Connection Details
            </Typography>
          </Grid>
        </Grid>
        {as2Details.as2Identifier != null && !displayMessage.visible && (
          <>
            <Grid container size={{xs:12}}>
              <Grid item size={{xs:2}}>
                <FormControl fullWidth>
                  <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                    Direction
                  </InputLabel>
                  <RenderDirection
                    value={getDirectionLabel(as2Details.direction)}
                  />
                </FormControl>
              </Grid>
              <Grid item size={{xs:2}}>
                <FormControl fullWidth>
                  <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                    Supplier Num
                  </InputLabel>
                  <Typography size="sm" component="p" gutterBottom>
                    {supplierNum}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item size={{xs:2}}>
                <FormControl fullWidth>
                  <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                    AS2 Identifier
                  </InputLabel>
                  <Typography size="sm" component="p" gutterBottom>
                    {as2Details.as2Identifier}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item size={{xs:2}}>
                <FormControl fullWidth>
                  <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                    Identity Name
                  </InputLabel>
                  <Typography size="sm" component="p" gutterBottom>
                    {as2Details.identityName}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item size={{xs:2}}>
                <FormControl fullWidth>
                  <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                    Profile Name
                  </InputLabel>
                  <Typography size="sm" component="p" gutterBottom>
                    {as2Details.profileName}
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container size={{xs:12}}>
              <Grid item size={{xs:6}}>
                <FormControl fullWidth>
                  <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                    AS2 Endpoint
                  </InputLabel>
                  <Typography size="sm" component="p" gutterBottom>
                    {as2Details.endPoint}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item size={{xs:6}}>
                <FormControl fullWidth>
                  <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                    TP Envelope
                  </InputLabel>
                  <Typography size="sm" component="p" gutterBottom>
                    {as2Details.envelopeName}
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container size={{xs:12}} mt={"5px"}>
              <Grid item size={{xs:2}}>
                <Typography
                  size="sm"
                  fontWeight="bold"
                  component="p"
                  gutterBottom
                >
                  Certificates Configured in Translator
                </Typography>
              </Grid>
            </Grid>

            {as2Details.caCertificate.map((cert) => {
              return (
                <CertComponent
                  key={"ca" + cert.certificateOrder}
                  order={cert.certificateOrder}
                  name="CA Cert"
                  certName={cert.certName}
                  goLiveDate={cert.goLiveDate}
                  notAfterDate={cert.notAfterDate}
                  showValidateButton={true}
                  endpoint={as2Details.endPoint}
                  remoteCAHandler={setRemoteCACert}
                  errorRemoteCAHandler={setErrorRemoteCACert}
                />
              );
            })}

            {as2Details.exchangeCertificate.map((cert) => {
              return (
                <CertComponent
                  key={"ex-" + cert.certificateOrder}
                  order={cert.certificateOrder}
                  name="Exchange Cert"
                  certName={cert.certName}
                  goLiveDate={cert.goLiveDate}
                  notAfterDate={cert.notAfterDate}
                  showValidateButton={false}
                  endpoint={as2Details.endPoint}
                />
              );
            })}
            {as2Details.signingCertificate.map((cert) => {
              return (
                <CertComponent
                  key={"sg-" + cert.certificateOrder}
                  order={cert.certificateOrder}
                  name="Signing Cert"
                  certName={cert.certName}
                  goLiveDate={cert.goLiveDate}
                  notAfterDate={cert.notAfterDate}
                  showValidateButton={false}
                  endpoint={as2Details.endPoint}
                />
              );
            })}
            {remoteCACert.certName !== null && (
              <>
                <Grid container size={{xs:12}} mt={"10px"}>
                  <Grid item size={{xs:2}}>
                    <Typography
                      size="sm"
                      fontWeight="bold"
                      component="p"
                      gutterBottom
                    >
                      Remote Certificates
                    </Typography>
                  </Grid>
                </Grid>
                <CertComponent
                  key={"rm-" + remoteCACert.certificateOrder}
                  order={remoteCACert.certificateOrder}
                  name="CA Cert"
                  certName={remoteCACert.certName}
                  goLiveDate={remoteCACert.goLiveDate}
                  notAfterDate={remoteCACert.notAfterDate}
                  showValidateButton={false}
                  endpoint={remoteCACert.certName}
                />
              </>
            )}
            {envelopes !== undefined && (
              <Grid container size={{xs:12}} mt={"10px"}>
                <Grid item size={{xs:2}}>
                  <Accordion sx={{ borderRadius: "0rem" }}>
                    <AccordionSummary>
                      TP Envelopes Linked to AS2 Connection
                      <Box
                        className="end-content"
                        aria-label="status-information"
                        sx={(theme) => ({
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          [theme.breakpoints.down("sm")]: {
                            flexGrow: "0",
                            marginLeft: "0.5rem",
                          },
                        })}
                      >
                        <CheckCircle
                          fontSize="small"
                          sx={{ color: "green", mr: 1 }}
                        />
                        <Typography variant="body2">
                          {envelopes.length}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container size={{xs:12}}>
                        {envelopes.map((envelope, index) => {
                          return (
                            <Grid item size={{xs:2}} key={envelope}>
                              <Typography
                                size="sm"
                                fontWeight="bold"
                                component="p"
                                gutterBottom
                              >
                                {envelope}
                              </Typography>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
};

const CertComponent = (props) => {
  const expirationDays =
    props.goLiveDate && props.notAfterDate
      ? getDiffDays(new Date(), props.notAfterDate)
      : null;
  const expirationLabel = getExpirationLabel(expirationDays);

  const { oktaAuth } = useOktaAuth();
  const as2Client = useAs2Client(oktaAuth.getAccessToken());
  const as2Endpoint = props.endpoint;

  const fetchValidateCertificate = async (hostname, port) => {
    return await as2Client.get(
      `api/certificates/cert-details?hostname=${hostname}&port=${port}`
    );
  };

  const onClickValidateCert = (event) => {
    event.preventDefault();
    const endpointElements = extractHostnamePort(as2Endpoint);
    fetchValidateCertificate(endpointElements.hostname, endpointElements.port)
      .then((response) => {
        props.remoteCAHandler({
          certificateOrder: 1,
          certName: endpointElements.hostname + ":" + endpointElements.port,
          goLiveDate: response.data.goLiveDate,
          notAfterDate: response.data.notAfterDate,
        });
      })
      .catch((reason) => {
        const message =
          typeof reason.response !== "undefined"
            ? reason.response.data.message
            : reason.message;
        props.errorRemoteCAHandler(message);
      });
  };

  return (
    <Card sx={{ width: "100%" }} variant="outlined" square={true}>
      <CardContent divider={"FuelDivider"}>
        <Grid container mb={"10px"}>
          <Grid item size={{xs:1}}>
            <FormControl fullWidth>
              <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                {props.name}
              </InputLabel>
              <Typography size="sm" component="p" gutterBottom></Typography>
            </FormControl>
          </Grid>
          <Grid item size={{xs:2}}>
            <FormControl fullWidth sx={{ alignItems: "center" }}>
              <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                Status
              </InputLabel>
              <RenderStatus value={expirationLabel} />
            </FormControl>
          </Grid>
          <Grid item size={{xs:4}}>
            <FormControl fullWidth>
              <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                Name
              </InputLabel>
              <Typography size="sm" component="p" gutterBottom>
                {props.certName}
              </Typography>
            </FormControl>
          </Grid>
          <Grid item size={{xs:1}}>
            <FormControl fullWidth sx={{ alignItems: "center" }}>
              <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                Order
              </InputLabel>
              <Typography size="sm" component="p" gutterBottom>
                {props.order}
              </Typography>
            </FormControl>
          </Grid>
          <Grid item size={{xs:1}}>
            <FormControl fullWidth sx={{ alignItems: "self-end" }}>
              <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                Valid from
              </InputLabel>
              <Typography size="sm" component="p" gutterBottom>
                {props.goLiveDate}
              </Typography>
            </FormControl>
          </Grid>
          <Grid item size={{xs:1}}>
            <FormControl fullWidth sx={{ alignItems: "self-end" }}>
              <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                Valid To
              </InputLabel>
              <Typography size="sm" component="p" gutterBottom>
                {props.notAfterDate}
              </Typography>
            </FormControl>
          </Grid>
          <Grid item size={{xs:2}}>
            <FormControl fullWidth sx={{ alignItems: "self-end" }}>
              <InputLabel style={{ fontWeight: "bold", fontSize: "small" }}>
                Expiration in Days
              </InputLabel>
              <Typography size="sm" component="p" gutterBottom>
                {expirationDays != null ? expirationDays : 0} Days
              </Typography>
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
      {props.showValidateButton && (
        <CardActions  sx={{
          display: 'flex',
          flexDirection: 'row-reverse',
          p: 3,

          bgcolor: 'text.disabled',
          borderRadius: 1,
        }}>
          <Button  style={{ 
                    borderColor:"#ebd534",
                    backgroundColor: "#ebd534",
                    color:"black"    
                   }} justifyContent="flex-end" variant="primary" onClick={onClickValidateCert}>
            Remote Cert Validation
          </Button>
        </CardActions>
      )}
    </Card>
  );
};
As2TPDetails.propTypes = {
  searchExecutedHandler: PropTypes.string,
  supplierNum: PropTypes.string,
  as2TPIdentifier: PropTypes.string,
  as2TPProfileName: PropTypes.string,
  envelopes: PropTypes.array,
};

export default As2TPDetails;
